import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../utils/cn";
import type { AgendaWeekSummary } from "../agendaTypes";
import {
  getFortalezaTodayIso,
  normalizeAgendaIsoDate,
} from "../agendaFiltersQuery";

type WeekStripProps = {
  selectedDateIso: string;
  weekSummary: AgendaWeekSummary;
  onSelectDate: (isoDate: string) => void;
  className?: string;
};

const WeekStrip: React.FC<WeekStripProps> = ({
  selectedDateIso,
  weekSummary,
  onSelectDate,
  className,
}) => {
  const todayIso = getFortalezaTodayIso();
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const normalizedSelectedDate =
    normalizeAgendaIsoDate(selectedDateIso) ?? selectedDateIso;

  const syncScrollState = useCallback(() => {
    const element = stripRef.current;
    if (!element) {
      return;
    }
    setCanScrollLeft(element.scrollLeft > 4);
    setCanScrollRight(
      element.scrollLeft + element.clientWidth < element.scrollWidth - 4
    );
  }, []);

  useEffect(() => {
    const element = stripRef.current;
    if (!element) {
      return;
    }
    syncScrollState();
    element.addEventListener("scroll", syncScrollState, { passive: true });
    window.addEventListener("resize", syncScrollState);
    return () => {
      element.removeEventListener("scroll", syncScrollState);
      window.removeEventListener("resize", syncScrollState);
    };
  }, [syncScrollState, weekSummary.days.length]);

  const moveDays = (direction: -1 | 1) => {
    stripRef.current?.scrollBy({
      left: direction * Math.max(stripRef.current.clientWidth * 0.7, 220),
      behavior: "smooth",
    });
  };

  return (
    <section className={cn("relative flex min-w-0 items-center", className)}>
      {canScrollLeft ? (
        <button
          type="button"
          aria-label="Dias anteriores"
          onClick={() => moveDays(-1)}
          className="absolute left-1 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] bg-white/95 text-[#4A5568] shadow-sm backdrop-blur-sm transition hover:bg-[#F7FAFC]"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      <div
        ref={stripRef}
        className="flex min-w-0 gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {weekSummary.days.map((day) => {
          const dayIso = normalizeAgendaIsoDate(day.date) ?? day.date;
          const isSelected = dayIso === normalizedSelectedDate;
          const isToday = dayIso === todayIso;
          const isPast = dayIso < todayIso;

          return (
            <button
              key={day.date}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectDate(day.date)}
              className={cn(
                "relative flex min-h-[72px] w-[76px] shrink-0 flex-col items-center justify-center rounded-xl border px-3 py-2.5 text-center transition",
                isSelected
                  ? "border-[#2D3748] bg-[#2D3748] text-white"
                  : isPast
                    ? "border-dashed border-[#E2E8F0] bg-[#F7FAFC] text-[#A0AEC0] hover:border-[#CBD5E0]"
                    : "border-[#E2E8F0] bg-white text-[#4A5568] hover:border-[#CBD5E0] hover:bg-[#FAFBFC]"
              )}
            >
              <span className="text-[10px] font-bold uppercase leading-none tracking-wide">
                {day.weekday_label}
              </span>
              <span className="mt-1.5 text-base font-bold leading-none">
                {day.day_of_month}
              </span>
              {day.pending ? (
                <span
                  className={cn(
                    "mt-2 h-2 w-10 animate-pulse rounded",
                    isSelected ? "bg-white/30" : "bg-[#E2E8F0]"
                  )}
                  aria-hidden
                />
              ) : (
                <span
                  className={cn(
                    "mt-1.5 text-[10px] font-medium leading-tight",
                    isSelected ? "text-white/90" : "text-[#718096]"
                  )}
                >
                  {day.visits_count} visitas
                </span>
              )}
              {isToday && !isSelected ? (
                <span
                  className="absolute bottom-1.5 h-1.5 w-1.5 rounded-full bg-[#c4552b]"
                  aria-hidden
                />
              ) : null}
            </button>
          );
        })}
      </div>
      {canScrollRight ? (
        <button
          type="button"
          aria-label="Próximos dias"
          onClick={() => moveDays(1)}
          className="absolute right-1 z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] bg-white/95 text-[#4A5568] shadow-sm backdrop-blur-sm transition hover:bg-[#F7FAFC]"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </section>
  );
};

export default WeekStrip;
